import React from 'react';
import { Box, Grid2 } from '@mui/material';
import { useGetContactViewDetailsById } from '../../API/hooks/ContactUsHook';
import { contactUsInitialValues } from './AddNewContactFacility';
import {
  CustomDialog,
  CustomDialogContent,
  CustomDivider,
  CustomIconButton,
  CustomTypography,
} from '@qler-view/shared_lib/src';
import CrossIcon from '@assets/images/Cross.svg';

export interface ViewContactFaciltyProps {
  open: boolean;
  onClose: () => void;
  contactId?: string;
}

const ViewContactFacility: React.FC<ViewContactFaciltyProps> = ({
  open,
  onClose,
  contactId,
}) => {
  const userId = localStorage.getItem('userId') ?? '0';
  const [initialValues, setInitialValues] = React.useState(
    contactUsInitialValues
  );

  const { ContactDetailsViewById, isLoading } = useGetContactViewDetailsById(
    userId,
    Number(contactId)
  );

  React.useEffect(() => {
    if (ContactDetailsViewById && !isLoading) {
      setInitialValues({
        id: ContactDetailsViewById?.id ?? 0,
        firstName: ContactDetailsViewById?.firstName ?? '',
        lastName: ContactDetailsViewById?.lastName ?? '',
        suffix: ContactDetailsViewById?.suffix ?? '',
        title: ContactDetailsViewById?.title ?? '',
        department: ContactDetailsViewById?.department ?? '',
        primaryEmail: ContactDetailsViewById?.primaryEmail ?? '',
        otherEmail: ContactDetailsViewById?.otherEmail ?? '',
        primaryPhone: ContactDetailsViewById?.primaryPhone ?? '',
        mobilePhone: ContactDetailsViewById?.mobilePhone ?? '',
        otherPhone: ContactDetailsViewById?.otherPhone ?? '',
        category: ContactDetailsViewById?.category ?? '',
        notes: ContactDetailsViewById?.notes ?? '',
      });
    } else {
      setInitialValues(contactUsInitialValues);
    }
  }, [ContactDetailsViewById, isLoading]);

  return (
    <CustomDialog
      id="view-contact-dialog"
      open={open}
      onClose={() => {
        setInitialValues(contactUsInitialValues);
        onClose();
      }}
      disableEscapeKeyDown={true}
      maxWidth="lg"
      fullWidth
    >
      <CustomDialogContent className="view-contact-daialog-content">
        <Box>
          <Box className="flex-justify-space-between">
            <CustomTypography
              id="view-contact-title"
              variant="h4"
              color="textPrimary"
            >
              Contact Details
            </CustomTypography>
            <CustomIconButton
              id="view-dialog-close"
              icon={<CrossIcon />}
              onClick={() => {
                setInitialValues(contactUsInitialValues);
                onClose();
              }}
            />
          </Box>

          <CustomTypography
            id="view-contact-sub-text"
            variant="body3"
            color="textDisabled"
          >
            Viewing contact information
          </CustomTypography>
        </Box>
        <Box>
          <Box className="pt-24">
            <Box>
              <Grid2 container>
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <CustomTypography
                    variant="h5"
                    className="text-align-left pb-20"
                    id="view-contact-personal-info-text"
                  >
                    Personal Information
                  </CustomTypography>
                </Grid2>
              </Grid2>
              <Box className="display-flex pb-24">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="view-contact-first-name-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      First Name
                    </CustomTypography>
                    <CustomTypography
                      id="view-contact-first-name-text"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.firstName || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="view-contact-last-name-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Last Name
                    </CustomTypography>
                    <CustomTypography
                      id="view-contact-last-name-text"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.lastName || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="view-contact-suffix-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Suffix
                    </CustomTypography>
                    <CustomTypography
                      id="view-contact-suffix-text"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.suffix || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                </Grid2>
              </Box>
              <CustomDivider />
              <Grid2 container className="pt-24">
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <CustomTypography
                    variant="h5"
                    className="text-align-left pb-16"
                    id="view-contact-professional-info-text"
                  >
                    Professional Information
                  </CustomTypography>
                </Grid2>
              </Grid2>
              <Box className="display-flex pb-24">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="view-contact-title-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Title
                    </CustomTypography>
                    <CustomTypography
                      id="view-contact-title-text"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.title || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="view-contact-department-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Department
                    </CustomTypography>
                    <CustomTypography
                      id="view-contact-department-text"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.department || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="view-contact-category-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Category
                    </CustomTypography>
                    <CustomTypography
                      id="view-contact-category-text"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.category || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                </Grid2>
              </Box>
              <CustomDivider />
              <Grid2 container className="pt-24">
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <CustomTypography
                    variant="h5"
                    className="text-align-left pb-16"
                    id="view-contact-info-text"
                  >
                    Contact Information
                  </CustomTypography>
                </Grid2>
              </Grid2>
              <Box className="display-flex pb-24">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="view-contact-primary-phone-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Primary Phone
                    </CustomTypography>
                    <CustomTypography
                      id="view-contact-primary-phone-text"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.primaryPhone || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="view-contact-mobile-phone-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Mobile Phone
                    </CustomTypography>
                    <CustomTypography
                      id="view-contact-mobile-phone-text"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.mobilePhone || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="view-contact-primary-email-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Primary Email
                    </CustomTypography>
                    <CustomTypography
                      id="view-contact-primary-email-text"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.primaryEmail || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                </Grid2>
              </Box>
              <CustomDivider />
              <Grid2 container className="pt-24">
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <CustomTypography
                    variant="h5"
                    className="text-align-left pb-16"
                    id="contact-notes-text"
                  >
                    Notes
                  </CustomTypography>
                </Grid2>
              </Grid2>
              <Box className="display-flex">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="contact-notes-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Notes
                    </CustomTypography>
                    <CustomTypography
                      id="contact-notes-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {initialValues.notes || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                </Grid2>
              </Box>
            </Box>
          </Box>
        </Box>
      </CustomDialogContent>
    </CustomDialog>
  );
};

export default ViewContactFacility;
